import * as Sentry from '@sentry/browser'
import { ApiResponseError, isApiClientError } from './ApiResponseError'
import { logApiClientError } from './logApiClientError'
import { isFetchNetworkError } from './isFetchNetworkError'

/**
 * Adds context to a failed API call to a Sentry scope: the response
 * when there was one, or the user's connection when there wasn't
 *
 * @param {import('@sentry/browser').Scope} scope
 * @param {unknown} error
 * @param {string} url
 */
export function addApiErrorContext(scope, error, url) {
  if (error instanceof ApiResponseError) {
    scope.setTag('api_status', String(error.details.status))
    scope.setContext('api_response', { ...error.details })
  } else if (isFetchNetworkError(error)) {
    scope.setTag('network_error', 'true')
    scope.setTag('online', String(navigator.onLine))
    scope.setContext('api_request', {
      url,
      online: navigator.onLine,
      visibility_state: document.visibilityState,
      ms_since_page_load: Math.round(performance.now()),
      connection:
        // @ts-ignore - not in every browser
        navigator.connection?.effectiveType || 'unknown',
    })
  }
}

/**
 * @typedef {object} CaptureApiErrorOptions
 * @property {string} [metric] - `metric` tag, defaults to 'api-error'
 * @property {string} [transactionName] - groups the error under a transaction
 * @property {Record<string, string>} [tags] - extra tags
 */

/**
 * Reports a failed API call
 * A 4xx as an `api-client-error-<status>` log, anything
 * else as an error with the response or connection details
 *
 * @param {unknown} error
 * @param {string} url - the endpoint called, e.g. '/api/chat/start'
 * @param {CaptureApiErrorOptions} [options]
 */
export function captureApiError(
  error,
  url,
  { metric = 'api-error', transactionName, tags = {} } = {},
) {
  // a 4xx means the request was refused, not that the API failed
  if (isApiClientError(error)) {
    logApiClientError(error)
    return
  }

  try {
    Sentry.captureException(error, (scope) => {
      if (transactionName) {
        scope.setTransactionName(transactionName)
      }
      scope.setTag('metric', metric)
      Object.entries(tags).forEach(([key, value]) => scope.setTag(key, value))
      addApiErrorContext(scope, error, url)
      return scope
    })
  } catch {
    // never let reporting break the page
  }
}
